<script lang="ts">
	import {
		Label,
		LabelV2,
		type DeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
		type LabelV2PropsTypes,
		type TripleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	// Components
	import VirtualBooleanButton from 'src/components/DevicesSettings/VirtualComponents/Components/VirtualBooleanButton.svelte';
	import VirtualButton from 'src/components/DevicesSettings/VirtualComponents/Components/VirtualButton.svelte';
	import ThermostatControl from './Thermostat/ThermostatControl.svelte';
	import ValveControl from './Valve/ValveControl.svelte';
	import SwitchControl from './Switch/SwitchControl.svelte';
	import WindowCoverControl from './WindowCover/WindowCoverControl.svelte';
	import EvChargerControl from './EvCharger/EvChargerControl.svelte';
	import ControlOnOff from '../Controls/Single/ControlOnOff.svelte';
	import InputsReaderVisualizer from '../Controls/Indicators/InputsReaderVisualizer.svelte';
	// Helpers
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	import { isXT1ComponentDevice } from 'src/helpers/xt1_helpers/template_helpers';
	import {
		sendRequest,
		isDefaultTemplateType,
	} from 'src/helpers/xt1_helpers/template_request_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { VCInterfaceStatus } from 'src/types/VirtualComponents';
	import { DeviceControl } from '@shelly/device-templates';

	export let device: Device;
	export let statuses: VCInterfaceStatus[];

	export let useV2Components = false;
	export let labelV2Props: Partial<LabelV2PropsTypes> = {};
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
		tripleDeviceButtonV2?: Partial<TripleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
		tripleDeviceButtonV2: { size: 's' },
	};

	$: templateType = statuses?.[0]?.templateType;
	$: defaultTemplateType = isDefaultTemplateType(statuses?.[0]?.serviceType);
</script>

{#if defaultTemplateType}
	<DeviceControl {device} {statuses} templateType={statuses[0]?.serviceType} {sendRequest} />
{:else if templateType === 'cover'}
	<WindowCoverControl {useV2Components} {device} {statuses} {devBtnsV2Props} />
{:else if templateType === 'switch' || templateType === 'light'}
	<SwitchControl
		{useV2Components}
		{device}
		{statuses}
		deviceButtonV2Props={devBtnsV2Props?.deviceButtonV2}
	/>
{:else if templateType === 'valve'}
	<ValveControl
		{useV2Components}
		{device}
		{statuses}
		deviceButtonV2Props={devBtnsV2Props?.deviceButtonV2}
	/>
{:else if templateType === 'thermostat'}
	<ThermostatControl {useV2Components} {device} {statuses} {devBtnsV2Props} />
{:else if templateType === 'ev-charger'}
	<EvChargerControl {device} {statuses} deviceButtonV2Props={devBtnsV2Props.deviceButtonV2} />
{:else if isXT1ComponentDevice(device)}
	{@const status = devices_status.getByRealId(device.id)}
	{@const settings = devices_settings.getByRealId(device.id)}
	{#if /^(relay|light)$/.test(device?.category)}
		<ControlOnOff
			{device}
			{useV2Components}
			{status}
			{settings}
			deviceButtonV2Props={devBtnsV2Props.deviceButtonV2}
		/>
	{:else if device?.category === 'inputs_reader'}
		<InputsReaderVisualizer
			{device}
			{status}
			{settings}
			{useV2Components}
			{labelV2Props}
			deviceButtonV2Props={devBtnsV2Props?.deviceButtonV2}
		/>
	{/if}
{:else}
	{@const btn = statuses.find((i) => i.hasAction)}
	{#if btn}
		{#if btn.vcType == 'boolean'}
			<VirtualBooleanButton
				{device}
				{useV2Components}
				componentID={btn.id}
				value={btn.event}
				icon="fa fa-power-off"
				deviceButtonV2Props={devBtnsV2Props.deviceButtonV2}
			/>
		{:else}
			<VirtualButton
				{device}
				componentID={btn.id}
				value={btn.event}
				{useV2Components}
				icon="fa fa-power-off"
				deviceButtonV2Props={devBtnsV2Props.deviceButtonV2}
			/>
		{/if}
	{:else}
		{@const mainLabel = statuses.find((i) => i.mainLabel)}
		{#if mainLabel}
			{#if useV2Components}
				<LabelV2
					class="align-self-center max-width-full"
					icon={mapIconSolidWeightToLight(mainLabel.icon || null)}
					iconColor={mainLabel.iconColor || null}
					value={mainLabel.value || ''}
					{...labelV2Props}
				/>
			{:else}
				<Label
					class="align-self-center max-width-full"
					icon={mainLabel.icon || null}
					iconColor={mainLabel.iconColor || null}
					value={mainLabel.value || ''}
					size={'l'}
				/>
			{/if}
		{/if}
	{/if}
{/if}
